"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Register, RegisterSchema } from "@/lib/validations";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { RegisterFormProps } from "@/components/auth/types";
import { PhoneInput } from "@/components/ui/phone-input";
import { LoadingButton } from "@/components/ui/loading-button";
import { PasswordStrengthIndicator } from "@/components/ui/password-strength-indicator";

export function RegisterForm({
  onSubmit,
  status,
  defaultValues,
  onEmailChange,
  onPasswordChange,
  clearPasswordsTrigger,
}: RegisterFormProps & { clearPasswordsTrigger?: number }) {
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const form = useForm<Register>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      email: defaultValues?.email || "",
      password: defaultValues?.password || "",
      confirm_password: defaultValues?.confirmPassword || "",
      first_name: "",
      last_name: "",
      phone: "",
    },
  });

  const { watch } = form;

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "email") onEmailChange?.(value.email as string);
      if (name === "password") onPasswordChange?.(value.password as string);

      // Check if passwords match
      if (value.password && value.confirm_password) {
        setPasswordsMatch(value.password === value.confirm_password);
      } else {
        setPasswordsMatch(false);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, onEmailChange, onPasswordChange]);

  useEffect(() => {
    if (clearPasswordsTrigger !== undefined) {
      form.setValue("password", "");
      form.setValue("confirm_password", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearPasswordsTrigger]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input {...field} placeholder='Enter your email' type='email' />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='password'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <div className='relative'>
                  <Input
                    {...field}
                    placeholder='Enter your password'
                    type='password'
                    variant='password'
                  />
                  {passwordsMatch && field.value && (
                    <Check className='w-4 h-4 absolute right-9 top-1/2 -translate-y-1/2 text-green-500' />
                  )}
                </div>
              </FormControl>
              <FormMessage />
              {field.value && (
                <PasswordStrengthIndicator
                  password={field.value}
                  className='mt-2'
                />
              )}
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='confirm_password'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm Password</FormLabel>
              <FormControl>
                <div className='relative'>
                  <Input
                    {...field}
                    placeholder='Confirm your password'
                    type='password'
                    variant='password'
                  />
                  {passwordsMatch && field.value && (
                    <Check className='w-4 h-4 absolute right-9 top-1/2 -translate-y-1/2 text-green-500' />
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className='flex justify-end'>
          <button
            type='button'
            className='text-xs text-primary flex items-center gap-1 hover:underline focus:outline-none'
            onClick={() => setShowMore((v) => !v)}
            aria-expanded={showMore}
            aria-controls='register-more-fields'
          >
            {showMore ? (
              <ChevronUp className='w-4 h-4' />
            ) : (
              <ChevronDown className='w-4 h-4' />
            )}
            {showMore ? "Hide additional fields" : "Show more options"}
          </button>
        </div>
        {showMore && (
          <div id='register-more-fields' className='space-y-2 animate-fade-in'>
            <FormField
              control={form.control}
              name='first_name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name (optional)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder='Enter your first name'
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='last_name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name (optional)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder='Enter your last name'
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='phone'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone (optional)</FormLabel>
                  <FormControl>
                    <PhoneInput
                      {...field}
                      value={field.value || ""}
                      onChange={field.onChange}
                      placeholder='Enter your phone number'
                      defaultCountry='AU'
                      international
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
        <LoadingButton
          type='submit'
          className={cn(
            "w-full",
            status === "executing" ? "animate-pulse" : ""
          )}
          loading={status === "executing"}
          loadingText='Checking...'
          disabled={status === "executing"}
        >
          Register
        </LoadingButton>
      </form>
    </Form>
  );
}
